import React, { useCallback, useEffect, useState } from 'react';
import { consentAPI, insightsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './VehicleList.css';

interface ConsentEntry {
  id: number;
  consentType: string;
  timestamp: string;
  acceptedByUsername: string;
  clientId: number | null;
  clientName: string | null;
  driverId: number | null;
  driverName: string | null;
  details: string | null;
  ipAddress: string | null;
}

interface Client {
  id: number;
  companyName: string;
}

const PAGE_SIZE = 50;

const ConsentRecords: React.FC = () => {
  const { isAdmin } = useAuth();

  const [entries,     setEntries]     = useState<ConsentEntry[]>([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [typeFilter,  setTypeFilter]  = useState('');
  const [clientId,    setClientId]    = useState('');
  const [page,        setPage]        = useState(1);
  const [clients,     setClients]     = useState<Client[]>([]);

  useEffect(() => {
    if (isAdmin) {
      insightsAPI.getClientsList()
        .then((r) => setClients(r.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const res = await consentAPI.getAudit({
        clientId:  clientId ? parseInt(clientId) : undefined,
        search:    search    || undefined,
        type:      typeFilter || undefined,
        page:      p,
        pageSize:  PAGE_SIZE,
      });
      setEntries(res.data.items);
      setTotal(res.data.total);
      setPage(p);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [clientId, search, typeFilter]);

  useEffect(() => {
    load(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="vehicle-list">
      <div className="page-header">
        <h1>Consent Records</h1>
      </div>

      <div className="card">
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
          Auditable record of all data consent events — GDPR login acceptance and driving licence data consents.
          {isAdmin ? ' Showing all clients; use the filters below to narrow results.' : ' Showing consent events recorded for your account.'}
        </p>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 200px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Search</label>
            <input
              type="text"
              placeholder="Username, driver, client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load(1)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Consent Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
            >
              <option value="">All types</option>
              <option value="GDPR_LOGIN">Portal Login Consent</option>
              <option value="DRIVER_LICENCE">Driver Licence</option>
            </select>
          </div>

          {isAdmin && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 180px' }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Client</label>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.companyName}</option>
                ))}
              </select>
            </div>
          )}

          <button
            className="btn btn-primary"
            onClick={() => load(1)}
            disabled={loading}
            style={{ padding: '8px 16px', fontSize: 14 }}
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Date &amp; Time</th>
                <th>User</th>
                {isAdmin && <th>Client</th>}
                <th>Name</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>
                    Loading consent records...
                  </td>
                </tr>
              )}
              {!loading && entries.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: '24px 0' }}>
                    No consent records found.
                  </td>
                </tr>
              )}
              {!loading && entries.map((e) => {
                const isGdpr = e.consentType === 'GDPR_LOGIN';
                return (
                  <tr key={e.id}>
                    <td>
                      <span style={{
                        padding: '3px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
                        background: isGdpr ? '#ede9fe' : '#ecfdf5',
                        color:      isGdpr ? '#5b21b6' : '#065f46',
                      }}>
                        {isGdpr ? '\uD83D\uDD10 Portal Login Consent' : '\uD83E\uDEAA Driver Licence'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                      {new Date(e.timestamp).toLocaleDateString('en-GB')}{' '}
                      {new Date(e.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ fontSize: 13 }}>{e.acceptedByUsername}</td>
                    {isAdmin && (
                      <td style={{ fontSize: 13 }}>
                        {e.clientName ?? <span style={{ color: '#9ca3af' }}>&mdash;</span>}
                      </td>
                    )}
                    <td style={{ fontSize: 13 }}>
                      {e.driverName ?? <span style={{ color: '#9ca3af' }}>&mdash;</span>}
                    </td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>
                      <span title={e.details ?? ''}>
                        {e.details ? (e.details.length > 70 ? e.details.slice(0, 70) + '\u2026' : e.details) : '\u2014'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              disabled={page === 1}
              onClick={() => load(page - 1)}
            >
              &larr; Prev
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Page {page} of {totalPages} &middot; {total} record{total !== 1 ? 's' : ''}
            </span>
            <button
              className="btn btn-secondary"
              disabled={page >= totalPages}
              onClick={() => load(page + 1)}
            >
              Next &rarr;
            </button>
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
          Showing {entries.length} of {total} consent record{total !== 1 ? 's' : ''}.
          All events are timestamped and stored for compliance purposes.
        </div>
      </div>
    </div>
  );
};

export default ConsentRecords;
